/**
 * ScamBlocker AI — Denetim İzi (/audit)
 * Org eylem günlüğü: action filtresi + sayfalama (limit/offset).
 */

import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useState } from 'react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface AuditLog {
  id: string;
  actor_type: string;
  actor_email?: string;
  action: string;
  target_type?: string;
  target_id?: string;
  reason_code?: string;
  ip_address?: string;
  created_at: string;
}

const PAGE_SIZE = 50;

export function AuditPage() {
  const [page, setPage] = useState(0);
  const [action, setAction] = useState('');

  const { data, isLoading, isError } = useQuery<{ logs: AuditLog[] }>({
    queryKey: ['audit', page, action],
    queryFn: async () =>
      (
        await apiClient.get(API.org.audit, {
          params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE, action: action || undefined },
        })
      ).data,
  });

  const logs = data?.logs ?? [];
  const canNext = logs.length === PAGE_SIZE;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <FileText size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Denetim İzi</h1>
        <span className={styles.spacer} />
        <input
          className={styles.input}
          placeholder="Eylem filtrele (ör. staff.login)"
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(0);
          }}
        />
      </div>

      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Denetim izi yüklenemedi.</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Eylem</th>
                <th>Aktör</th>
                <th>Hedef</th>
                <th>Gerekçe</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className={styles.row}>
                  <td className={styles.mono}>{new Date(l.created_at).toLocaleString('tr-TR')}</td>
                  <td>
                    <span className={styles.badge} style={{ color: 'var(--color-primary)' }}>
                      {l.action}
                    </span>
                  </td>
                  <td className={styles.mono}>{l.actor_email ?? l.actor_type}</td>
                  <td>
                    {l.target_type
                      ? `${l.target_type}${l.target_id ? ` · ${l.target_id.slice(0, 8)}` : ''}`
                      : '—'}
                  </td>
                  <td>{l.reason_code ?? '—'}</td>
                  <td className={styles.mono}>{l.ip_address ?? '—'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.empty}>
                    Kayıt bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.formRow} style={{ justifyContent: 'center' }}>
        <button
          type="button"
          className={styles.actionBtn}
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          <ChevronLeft size={16} />
        </button>
        <span className={styles.mono} style={{ alignSelf: 'center' }}>
          Sayfa {page + 1}
        </span>
        <button
          type="button"
          className={styles.actionBtn}
          disabled={!canNext}
          onClick={() => setPage((p) => p + 1)}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
